package com.evoapps.evoshift.domain.repository

import com.evoapps.evoshift.domain.billing.BillingCustomer
import com.evoapps.evoshift.domain.billing.BillingException
import com.evoapps.evoshift.domain.billing.BillingStore
import com.evoapps.evoshift.domain.billing.PlanOffer
import kotlinx.coroutines.flow.StateFlow

/**
 * The store side of the owner's plan (SH-38): RevenueCat on Android and iOS (Google Play,
 * App Store) and RevenueCat Web Billing on the web, as in EVoLabels. Implementations live in
 * composeApp per platform; without a RevenueCat key for the platform [isConfigured] is false and
 * every call fails with [BillingException.Reason.NOT_CONFIGURED].
 *
 * The RevenueCat customer id is the owner's Firebase uid, so the webhook can mirror the
 * entitlements onto `owners/{uid}/billing/revenuecat` for the other platforms.
 */
interface BillingRepository {
    val isConfigured: Boolean

    /** The store this platform sells in; decides how a running plan is changed (PlanChange). */
    val store: BillingStore get() = BillingStore.OTHER

    /** What the store SDK knows about the identified customer; empty before [identify]. */
    val customer: StateFlow<BillingCustomer>

    /** Logs the store SDK in as [uid] (the Firebase uid), or out with null. Never fails. */
    suspend fun identify(uid: String?)

    /** The packages of the paywall's offering (Plans.OFFERING_ID) with store prices. */
    suspend fun getOffers(): Result<List<PlanOffer>>

    /** Opens the store's purchase sheet; fails with [BillingException] (CANCELLED when closed). */
    suspend fun purchase(offer: PlanOffer): Result<Unit>

    /** Asks the store for purchases this store account already owns. */
    suspend fun restorePurchases(): Result<Unit>

    /** Opens where the subscription is managed (store page or [managementUrl]). */
    suspend fun manageSubscription(managementUrl: String?): Result<Unit>
}
